import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import DateFromTo from "../../../widgets/DateFromTo/DateFromTo";
import { client as adminClient } from "../../../utils/adminNetwork";
import "./ProductAnalytics.scss";

const PERIOD_PRESETS = [
  { id: "today", label: "Сегодня" },
  { id: "yesterday", label: "Вчера" },
  { id: "7d", label: "Последние 7 дней" },
  { id: "30d", label: "Последние 30 дней" },
  { id: "90d", label: "Последние 90 дней" },
  { id: "custom", label: "Произвольный период" },
];

const DOMAIN_OPTIONS = [
  { id: "all", label: "Все домены" },
  { id: "lk.programmatic.ru", label: "lk.programmatic.ru" },
  { id: "lk.retargeo.ru", label: "lk.retargeo.ru" },
];

const FUNNEL_SHORT_NAMES = {
  visited_signup: "Визит",
  registered: "Регистрация",
  logged_in: "Вход в ЛК",
  metrika: "Метрика",
  campaign_start: "Старт РК",
  deposit: "Депозит",
  dmp: "DMP",
  launched: "Запуск",
};

const ACCENT = "#3F73F9";
const ACCENT_RGB = "63, 115, 249";

const toDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const resolvePeriodRange = (period, customRange) => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = new Date(end);
  if (period === "today") {
    return { from: toDateStr(start), to: toDateStr(end) };
  }
  if (period === "yesterday") {
    start.setDate(start.getDate() - 1);
    return { from: toDateStr(start), to: toDateStr(start) };
  }
  if (period === "7d") start.setDate(start.getDate() - 6);
  else if (period === "30d") start.setDate(start.getDate() - 29);
  else if (period === "90d") start.setDate(start.getDate() - 89);
  else if (period === "custom") {
    const [from, to] = customRange || [];
    if (from && to) return { from: toDateStr(from), to: toDateStr(to) };
    start.setDate(start.getDate() - 29);
  }
  return { from: toDateStr(start), to: toDateStr(end) };
};

const flattenTree = (nodes, depth = 0, parentKey = null) => {
  const rows = [];
  nodes.forEach((node) => {
    rows.push({
      ...node,
      depth,
      parentKey,
      hasChildren: Boolean(node.children?.length),
    });
    if (node.children?.length) {
      rows.push(...flattenTree(node.children, depth + 1, node.key));
    }
  });
  return rows;
};

const FunnelTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload;
  if (!item) return null;
  return (
    <div className="product-analytics__chart-tooltip">
      <strong>{item.name}</strong>
      <div>
        <span>Дошли</span>
        <b>{item.value.toLocaleString("ru-RU")}</b>
      </div>
      {item.key !== "visited_signup" && item.rate != null && (
        <div>
          <span>От регистраций</span>
          <b>{item.rate}%</b>
        </div>
      )}
      {item.toNext != null && (
        <div>
          <span>До следующего шага</span>
          <b>
            {item.toNext}% · −{item.lostNext}
          </b>
        </div>
      )}
    </div>
  );
};

const FunnelBarLabel = (props) => {
  const { x = 0, y = 0, width = 0, value } = props;
  if (value == null) return null;
  return (
    <text
      x={x + width / 2}
      y={y - 8}
      fill="#42454C"
      fontSize={12}
      fontWeight={700}
      textAnchor="middle"
      dominantBaseline="auto"
      pointerEvents="none"
    >
      {Number(value).toLocaleString("ru-RU")}
    </text>
  );
};

const ProductAnalytics = () => {
  const [period, setPeriod] = useState("30d");
  const [customRange, setCustomRange] = useState([null, null]);
  const [signupDomain, setSignupDomain] = useState("all");
  const [compareStage, setCompareStage] = useState("campaign_start");
  const [selectedFunnelKey, setSelectedFunnelKey] = useState("campaign_start");
  const [expanded, setExpanded] = useState({
    campaign_start: true,
    tab_ads: false,
    tab_audience: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState({ steps: [], extras: [] });
  const [retention, setRetention] = useState({ days: [], rows: [] });
  const [dropoffs, setDropoffs] = useState([]);
  const [compareRows, setCompareRows] = useState([]);
  const [clientsModal, setClientsModal] = useState(null);
  const [clientsModalTab, setClientsModalTab] = useState("passed");
  const [clientsPage, setClientsPage] = useState(1);
  const [clientsData, setClientsData] = useState({
    items: [],
    total: 0,
    pages: 1,
  });
  const [clientsLoading, setClientsLoading] = useState(false);

  const range = useMemo(
    () => resolvePeriodRange(period, customRange),
    [period, customRange]
  );

  const queryBase = useMemo(() => {
    const params = new URLSearchParams({
      date_from: range.from,
      date_to: range.to,
      signup_domain: signupDomain,
    });
    return params.toString();
  }, [range, signupDomain]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [overviewRes, funnelRes, retentionRes, dropoffsRes, compareRes] =
        await Promise.all([
          adminClient.get(`/api/v1/admin/product-analytics/overview?${queryBase}`),
          adminClient.get(`/api/v1/admin/product-analytics/funnel?${queryBase}`),
          adminClient.get(`/api/v1/admin/product-analytics/retention?${queryBase}`),
          adminClient.get(`/api/v1/admin/product-analytics/dropoffs?${queryBase}`),
          adminClient.get(
            `/api/v1/admin/product-analytics/compare?${queryBase}&stage=${compareStage}`
          ),
        ]);
      setOverview(overviewRes.data);
      setFunnel(funnelRes.data || { steps: [], extras: [] });
      setRetention(retentionRes.data || { days: [], rows: [] });
      setDropoffs(dropoffsRes.data?.items || []);
      setCompareRows(compareRes.data?.rows || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        (typeof detail === "string" && detail) ||
          err?.message ||
          "Не удалось загрузить аналитику"
      );
    } finally {
      setLoading(false);
    }
  }, [queryBase, compareStage]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const flatSteps = useMemo(() => flattenTree(funnel.steps || []), [funnel.steps]);

  const compareOptions = useMemo(
    () =>
      flatSteps
        .filter((step) => step.key !== "visited_signup")
        .map((step) => ({
          id: step.key,
          label: `${"— ".repeat(step.depth)}${step.label}`,
        })),
    [flatSteps]
  );

  const funnelRows = useMemo(
    () =>
      flatSteps.map((step) => ({
        ...step,
        count: step.count || 0,
        fromRegistered: step.fromRegistered ?? 0,
        fromPrev: step.fromPrev ?? 0,
        dropOff: step.dropOff ?? 0,
      })),
    [flatSteps]
  );

  const visibleRows = useMemo(
    () =>
      funnelRows.filter((row) => {
        if (row.depth === 0) return true;
        if (row.depth === 1) return expanded[row.parentKey];
        const parent = flatSteps.find((item) => item.key === row.parentKey);
        return expanded[parent?.parentKey] && expanded[row.parentKey];
      }),
    [funnelRows, expanded, flatSteps]
  );

  const topLevelRows = useMemo(
    () => funnelRows.filter((row) => row.depth === 0),
    [funnelRows]
  );

  const mainProblem = useMemo(() => {
    const ranked = topLevelRows
      .slice(0, -1)
      .map((row, index) => ({
        ...row,
        lostToNext: Math.max(0, row.count - (topLevelRows[index + 1]?.count || 0)),
      }))
      .filter((row) => row.lostToNext > 0)
      .sort((a, b) => b.lostToNext - a.lostToNext);
    return ranked[0] || null;
  }, [topLevelRows]);

  const funnelChartData = useMemo(
    () =>
      topLevelRows.map((row, index) => {
        const next = topLevelRows[index + 1];
        const isActive = selectedFunnelKey === row.key || compareStage === row.key;
        const isProblem = row.key === mainProblem?.key;
        const softAlpha = Math.max(0.32, 0.9 - index * 0.07);
        return {
          key: row.key,
          name: row.label,
          shortName: FUNNEL_SHORT_NAMES[row.key] || row.label,
          value: row.count,
          rate: row.key === "visited_signup" ? null : row.fromRegistered,
          toNext: next ? next.fromPrev : null,
          lostNext: next ? Math.max(0, row.count - next.count) : null,
          fill:
            isActive || isProblem
              ? ACCENT
              : `rgba(${ACCENT_RGB}, ${softAlpha})`,
          stroke: isActive ? "#2A56D6" : "transparent",
          strokeWidth: isActive ? 1.5 : 0,
        };
      }),
    [topLevelRows, selectedFunnelKey, compareStage, mainProblem]
  );

  const compareLabel =
    flatSteps.find((step) => step.key === compareStage)?.label || compareStage;

  const loadClients = useCallback(
    async (stage, group, page = 1) => {
      setClientsLoading(true);
      try {
        const { data } = await adminClient.get(
          `/api/v1/admin/product-analytics/clients?${queryBase}&stage=${stage}&group=${group}&page=${page}&page_size=10`
        );
        setClientsData(data);
        setClientsPage(data.page || page);
      } catch (_err) {
        setClientsData({ items: [], total: 0, pages: 1 });
      } finally {
        setClientsLoading(false);
      }
    },
    [queryBase]
  );

  const openClientsModal = (row) => {
    setClientsModal({
      key: row.key,
      label: row.label,
      count: row.count,
      dropOff: row.dropOff,
    });
    setClientsModalTab("passed");
    setClientsPage(1);
  };

  const closeClientsModal = () => setClientsModal(null);

  useEffect(() => {
    if (!clientsModal) return;
    loadClients(clientsModal.key, clientsModalTab, clientsPage);
  }, [clientsModal, clientsModalTab, clientsPage, loadClients]);

  const toggleExpand = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleFunnelClick = (key) => {
    setSelectedFunnelKey(key);
    if (key !== "visited_signup") setCompareStage(key);
  };

  const periodCaption = () => {
    if (period !== "custom") {
      return PERIOD_PRESETS.find((item) => item.id === period)?.label;
    }
    const [from, to] = customRange;
    if (from && to) {
      return `${from.toLocaleDateString("ru-RU")} — ${to.toLocaleDateString("ru-RU")}`;
    }
    return "Произвольный период";
  };

  const visited = overview?.visited ?? 0;
  const registered = overview?.registered ?? 0;
  const campaignStart = overview?.campaignStart ?? 0;
  const launched = overview?.launched ?? 0;
  const retentionDays = retention.days || [];

  return (
    <div className="product-analytics">
      <div className="product-analytics__toolbar">
        <div className="product-analytics__header">
          <div>
            <h1>Аналитика</h1>
            <p className="product-analytics__subtitle">
              Когорта: от визита на регистрацию до запуска кампании
            </p>
          </div>
        </div>
        <div className="product-analytics__filters">
          <div className="product-analytics__period-pills">
            {DOMAIN_OPTIONS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={
                  signupDomain === item.id
                    ? "product-analytics__pill is-active"
                    : "product-analytics__pill"
                }
                onClick={() => setSignupDomain(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="product-analytics__period-pills">
            {PERIOD_PRESETS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={
                  period === item.id
                    ? "product-analytics__pill is-active"
                    : "product-analytics__pill"
                }
                onClick={() => setPeriod(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          {period === "custom" && (
            <div className="product-analytics__custom-dates">
              <DateFromTo handleChange={(rangeValue) => setCustomRange(rangeValue)} />
            </div>
          )}
        </div>
      </div>

      {error && <div className="product-analytics__error">{error}</div>}
      {loading && <div className="product-analytics__loading">Загрузка…</div>}

      <div className="product-analytics__summary">
        <div className="product-analytics__kpi">
          <div className="product-analytics__kpi-icon product-analytics__kpi-icon--cohort">
            <GroupOutlinedIcon fontSize="small" />
          </div>
          <span className="product-analytics__kpi-label">Визиты на регистрацию</span>
          <strong>{visited.toLocaleString("ru-RU")}</strong>
          <span className="product-analytics__kpi-hint">{periodCaption()}</span>
        </div>
        <div className="product-analytics__kpi">
          <div className="product-analytics__kpi-icon product-analytics__kpi-icon--launch">
            <LoginOutlinedIcon fontSize="small" />
          </div>
          <span className="product-analytics__kpi-label">Зарегистрировались</span>
          <strong>{overview?.registeredRate ?? 0}%</strong>
          <span className="product-analytics__kpi-hint">
            {registered} из {visited || registered}
          </span>
        </div>
        <div className="product-analytics__kpi">
          <div className="product-analytics__kpi-icon product-analytics__kpi-icon--impressions">
            <CampaignOutlinedIcon fontSize="small" />
          </div>
          <span className="product-analytics__kpi-label">Начали создавать РК</span>
          <strong>{overview?.campaignStartRate ?? 0}%</strong>
          <span className="product-analytics__kpi-hint">
            {campaignStart} из {registered}
          </span>
        </div>
        <div className="product-analytics__kpi">
          <div className="product-analytics__kpi-icon product-analytics__kpi-icon--stats">
            <RocketLaunchOutlinedIcon fontSize="small" />
          </div>
          <span className="product-analytics__kpi-label">Запустили кампанию</span>
          <strong>{overview?.launchedRate ?? 0}%</strong>
          <span className="product-analytics__kpi-hint">
            {launched} из {registered}
          </span>
        </div>
      </div>

      <section className="product-analytics__section product-analytics__section--funnel">
        <div className="product-analytics__section-head">
          <div>
            <h2>Воронка клиентов</h2>
            <p className="product-analytics__section-desc">
              Столбцы — сколько дошли, линия — % от регистраций. Клик по столбцу открывает
              сравнение. Мастер кампании — в таблице (+ / −).
            </p>
          </div>
        </div>

        <div className="product-analytics__funnel-chart-wrap">
          <div className="product-analytics__funnel-legend">
            <span className="product-analytics__funnel-legend-item">
              <i className="is-bar" /> Клиентов на шаге
            </span>
            <span className="product-analytics__funnel-legend-item">
              <i className="is-line" /> % от регистраций
            </span>
            <span className="product-analytics__funnel-legend-item">
              <i className="is-active" /> Выбранный / сильный отвал
            </span>
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart
              data={funnelChartData}
              margin={{ top: 28, right: 18, left: 4, bottom: 8 }}
              barCategoryGap="28%"
            >
              <CartesianGrid strokeDasharray="3 6" vertical={false} stroke="#EEF0F4" />
              <XAxis
                dataKey="shortName"
                interval={0}
                tick={{ fontSize: 12, fill: "#42454C", fontWeight: 600 }}
                axisLine={{ stroke: "rgba(66, 69, 76, 0.12)" }}
                tickLine={false}
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 11, fill: "rgba(66, 69, 76, 0.55)" }}
                width={40}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[0, 100]}
                tick={{ fontSize: 11, fill: "rgba(66, 69, 76, 0.55)" }}
                width={36}
                unit="%"
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                cursor={{ fill: "rgba(63, 115, 249, 0.06)" }}
                content={<FunnelTooltip />}
              />
              <Bar
                yAxisId="left"
                dataKey="value"
                name="Клиентов"
                radius={[6, 6, 0, 0]}
                maxBarSize={48}
                cursor="pointer"
                isAnimationActive={false}
                onClick={(data) => {
                  const key = data?.payload?.key || data?.key;
                  if (key) handleFunnelClick(key);
                }}
              >
                <LabelList dataKey="value" position="top" content={<FunnelBarLabel />} />
                {funnelChartData.map((entry) => (
                  <Cell
                    key={entry.key}
                    fill={entry.fill}
                    stroke={entry.stroke}
                    strokeWidth={entry.strokeWidth}
                  />
                ))}
              </Bar>
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="rate"
                name="% от регистраций"
                stroke={ACCENT}
                strokeWidth={2}
                connectNulls
                isAnimationActive={false}
                dot={{ r: 3.5, fill: ACCENT, stroke: "#fff", strokeWidth: 2 }}
                activeDot={{ r: 5, fill: ACCENT }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="product-analytics__funnel-table-wrap product-analytics__funnel-table-wrap--wide">
          <table className="product-analytics__table">
            <thead>
              <tr>
                <th>Шаг</th>
                <th>Клиентов</th>
                <th>% от регистраций</th>
                <th>С шага выше</th>
                <th>Отвалилось</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr
                  key={row.key}
                  className={
                    selectedFunnelKey === row.key || compareStage === row.key
                      ? `is-selected depth-row-${row.depth}`
                      : `depth-row-${row.depth}`
                  }
                  onClick={() => handleFunnelClick(row.key)}
                >
                  <td>
                    <span className={`product-analytics__tree-label depth-${row.depth}`}>
                      {row.hasChildren && (
                        <button
                          type="button"
                          className="product-analytics__nest-toggle"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(row.key);
                          }}
                        >
                          {expanded[row.key] ? "−" : "+"}
                        </button>
                      )}
                      {row.label}
                    </span>
                  </td>
                  <td>{row.count.toLocaleString("ru-RU")}</td>
                  <td>
                    {row.key === "visited_signup" ? "—" : `${row.fromRegistered}%`}
                  </td>
                  <td>{row.key === "visited_signup" ? "—" : `${row.fromPrev}%`}</td>
                  <td>
                    {row.key === "visited_signup" || row.dropOff <= 0
                      ? "—"
                      : `−${row.dropOff}`}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="product-analytics__clients-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openClientsModal(row);
                      }}
                    >
                      Клиенты
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="product-analytics__section">
        <div className="product-analytics__section-head">
          <div>
            <h2>Дополнительно по когорте</h2>
            <p className="product-analytics__section-desc">
              Повторные действия - не шаги воронки, но показывают глубину использования
            </p>
          </div>
        </div>
        <div className="product-analytics__extras">
          {(funnel.extras || []).map((item) => (
            <div key={item.key} className="product-analytics__extra-card">
              <span>{item.label}</span>
              <strong>{Number(item.count || 0).toLocaleString("ru-RU")}</strong>
              <em>{item.rate}% от зарегистрированных</em>
            </div>
          ))}
        </div>
      </section>

      <section className="product-analytics__chart-card product-analytics__chart-card--flush">
        <div className="product-analytics__chart-head">
          <div>
            <h2>Возвращаются ли в кабинет</h2>
            <p>
              Из клиентов, зарегистрировавшихся в периоде, какой % заходил в ЛК через N дней
            </p>
          </div>
        </div>
        <div
          className="product-analytics__retention"
          style={{
            gridTemplateColumns: `110px repeat(${Math.max(retentionDays.length, 1)}, minmax(0, 1fr))`,
          }}
        >
          <div className="product-analytics__heatmap-corner" />
          {retentionDays.map((day) => (
            <div key={day} className="product-analytics__heatmap-label">
              {day}
            </div>
          ))}
          {(retention.rows || []).map((row) => (
            <div key={row.cohort} className="product-analytics__heatmap-row">
              <div className="product-analytics__heatmap-day">
                <span>{row.cohort}</span>
                <small>{row.size} чел.</small>
              </div>
              {(row.values || []).map((value, index) => {
                const intensity = Math.min(value / 100, 1);
                return (
                  <div
                    key={`${row.cohort}-${index}`}
                    className="product-analytics__heatmap-cell"
                    style={{
                      background: `rgba(63, 115, 249, ${0.08 + intensity * 0.82})`,
                      color: intensity > 0.45 ? "#ffffff" : "#1e3a8a",
                    }}
                    title={`${row.cohort}, ${retentionDays[index]}: ${value}%`}
                  >
                    {value}%
                  </div>
                );
              })}
            </div>
          ))}
          {!retention.rows?.length && (
            <div className="product-analytics__empty-inline">Нет данных retention</div>
          )}
        </div>
      </section>

      <section className="product-analytics__section">
        <div className="product-analytics__section-head">
          <div>
            <h2>Где и почему отваливаются</h2>
            <p className="product-analytics__section-desc">
              Самые большие потери между шагами воронки
            </p>
          </div>
        </div>
        <div className="product-analytics__reason-list">
          {dropoffs.map((item, index) => (
            <div key={`${item.fromKey}-${item.toKey}`} className="product-analytics__reason-card">
              <div className="product-analytics__reason-rank">
                {String(index + 1).padStart(2, "0")}
              </div>
              <div className="product-analytics__reason-main">
                <strong>{item.step}</strong>
                <p>
                  Потеряли <b>{Number(item.lost || 0).toLocaleString("ru-RU")}</b>
                </p>
                <span>{item.tip}</span>
              </div>
              <div
                className="product-analytics__reason-meter"
                aria-hidden="true"
                title="Доля потери от предыдущего шага"
              >
                <div style={{ width: `${item.share}%` }} />
              </div>
              <div className="product-analytics__reason-stat">
                <strong>{item.share}%</strong>
                <em>доля потери</em>
              </div>
            </div>
          ))}
          {!dropoffs.length && (
            <div className="product-analytics__empty-inline">Нет заметных разрывов</div>
          )}
        </div>
      </section>

      <section className="product-analytics__section product-analytics__section--compare">
        <div className="product-analytics__section-head">
          <div>
            <h2>Сравнения</h2>
            <p className="product-analytics__compare-caption">
              Выберите этап. Смотрим, какие шаги проходили те, кто{" "}
              <strong>дошёл до «{compareLabel}»</strong>, и те, кто до него не дошёл.
            </p>
          </div>
          <div className="product-analytics__stage-select">
            <label htmlFor="compare-stage">Этап</label>
            <select
              id="compare-stage"
              value={compareStage}
              onChange={(e) => {
                setCompareStage(e.target.value);
                setSelectedFunnelKey(e.target.value);
              }}
            >
              {compareOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="product-analytics__funnel-table-wrap product-analytics__funnel-table-wrap--wide">
          <table className="product-analytics__table product-analytics__table--compare">
            <thead>
              <tr>
                <th>Какой шаг прошли</th>
                <th>Среди дошедших до этапа</th>
                <th>Среди не дошедших</th>
                <th>Разница</th>
              </tr>
            </thead>
            <tbody>
              {compareRows.map((row) => (
                <tr key={row.key} className={`depth-row-${row.depth}`}>
                  <td>
                    <span className={`product-analytics__tree-label depth-${row.depth}`}>
                      {row.label}
                    </span>
                  </td>
                  <td>{row.progressed}%</td>
                  <td>{row.stuck}%</td>
                  <td className={row.diff > 0 ? "is-positive" : row.diff < 0 ? "is-negative" : ""}>
                    {row.diff > 0 ? "+" : ""}
                    {row.diff} п.п.
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {clientsModal && (
        <div
          className="product-analytics__modal-backdrop"
          onClick={closeClientsModal}
          role="presentation"
        >
          <div
            className="product-analytics__modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="product-analytics-clients-title"
          >
            <div className="product-analytics__modal-head">
              <div>
                <h2 id="product-analytics-clients-title">
                  Клиенты · {clientsModal.label}
                </h2>
                <p>Когорта за период · по 10 записей на страницу</p>
              </div>
              <button
                type="button"
                className="product-analytics__modal-close"
                onClick={closeClientsModal}
              >
                Закрыть
              </button>
            </div>

            <div className="product-analytics__modal-tabs">
              <button
                type="button"
                className={
                  clientsModalTab === "passed"
                    ? "product-analytics__modal-tab is-active"
                    : "product-analytics__modal-tab"
                }
                onClick={() => {
                  setClientsModalTab("passed");
                  setClientsPage(1);
                }}
              >
                Дошли · {clientsModal.count.toLocaleString("ru-RU")}
              </button>
              {clientsModal.key !== "visited_signup" && (
                <button
                  type="button"
                  className={
                    clientsModalTab === "stuck"
                      ? "product-analytics__modal-tab is-active"
                      : "product-analytics__modal-tab"
                  }
                  onClick={() => {
                    setClientsModalTab("stuck");
                    setClientsPage(1);
                  }}
                >
                  Не дошли · {clientsModal.dropOff.toLocaleString("ru-RU")}
                </button>
              )}
            </div>

            <div className="product-analytics__modal-body">
              {clientsLoading ? (
                <div className="product-analytics__loading">Загрузка клиентов…</div>
              ) : (
                <table className="product-analytics__table product-analytics__table--compare product-analytics__table--modal">
                  <thead>
                    <tr>
                      <th>Клиент</th>
                      <th>Email</th>
                      <th>Регистрация</th>
                      <th>Метрика</th>
                      <th>Начал РК</th>
                      <th>Депозит</th>
                      <th>DMP</th>
                      <th>Запуск</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(clientsData.items || []).map((item) => (
                      <tr key={item.id}>
                        <td>
                          {[item.firstName, item.surname].filter(Boolean).join(" ") || "—"}
                        </td>
                        <td>{item.email}</td>
                        <td>
                          {item.createdAt
                            ? new Date(item.createdAt).toLocaleDateString("ru-RU")
                            : "—"}
                        </td>
                        <td>{item.flags?.metrika ? "Да" : "Нет"}</td>
                        <td>{item.flags?.campaign_start ? "Да" : "Нет"}</td>
                        <td>{item.flags?.deposit ? "Да" : "Нет"}</td>
                        <td>{item.flags?.dmp ? "Да" : "Нет"}</td>
                        <td>{item.flags?.launched ? "Да" : "Нет"}</td>
                        <td>
                          <a
                            className="product-analytics__clients-btn"
                            href={`/admin/clients?user_id=${item.id}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Открыть
                          </a>
                        </td>
                      </tr>
                    ))}
                    {!clientsData.items?.length && (
                      <tr>
                        <td colSpan={9}>Нет клиентов в этой группе</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
              <div className="product-analytics__pager">
                <button
                  type="button"
                  disabled={clientsPage <= 1}
                  onClick={() => setClientsPage((p) => Math.max(1, p - 1))}
                >
                  Назад
                </button>
                <span>
                  {clientsPage} / {clientsData.pages || 1} · всего {clientsData.total || 0}
                </span>
                <button
                  type="button"
                  disabled={clientsPage >= (clientsData.pages || 1)}
                  onClick={() => setClientsPage((p) => p + 1)}
                >
                  Далее
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductAnalytics;
